import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Formik, Form, FormikHelpers } from 'formik';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { colors } from '../../theme/theme';
import { useCategory } from '../../hooks/useCategory';
import { useBusiness, CreateBusinessRequest } from '../../hooks/useBusiness';
import { BusinessPlaceDetails } from '../../hooks/useBusinessSearch';
import { useAuthService } from '../../hooks/useAuthService';
import { landingPath } from '../../auth/session';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryByName } from '../../constants/countries';
import { getStatesByCountryId, getStateByName } from '../../constants/statesData';
import { geocodeAddress, buildFullAddress, areAddressesSame, AddressComponents } from '../../utils/geocoding';
import {
  addBusinessSchema,
  initialValues,
  AddBusinessFormValues,
} from './schemas/addBusinessSchema';

interface LocationState {
  placeDetails?: BusinessPlaceDetails;
}

const OTHER_CATEGORY_NAME = 'Other';

const AddBusinessScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const placeDetails = state?.placeDetails;

  const { categories, isLoading: isCategoriesLoading } = useCategory();
  const { createBusiness, isLoading: isCreating } = useBusiness();
  const { getUserRole } = useAuthService();

  // UI state
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error'>('error');
  const [isOtherCategoryMode, setIsOtherCategoryMode] = React.useState<boolean>(false);

  // Find the "Other" category
  const otherCategory = useMemo(() => {
    return categories.find((cat) => cat.name.toLowerCase() === OTHER_CATEGORY_NAME.toLowerCase());
  }, [categories]);

  // Filter out "Other" category for dropdown
  const categoryOptions = useMemo(() => {
    return categories.filter(
      (cat) => cat.name.toLowerCase() !== OTHER_CATEGORY_NAME.toLowerCase()
    );
  }, [categories]);

  // Create initial form values from placeDetails
  const formInitialValues = useMemo((): AddBusinessFormValues => {
    if (!placeDetails) {
      return initialValues;
    }

    const cleanPhone = placeDetails.phoneNumber?.replace(/\D/g, '') || '';
    const country = placeDetails.country ? getCountryByName(placeDetails.country) : null;
    const countryId = country ? String(country.id) : '';
    const countryName = country ? country.name : placeDetails.country || '';
    const phoneCountryCode = country ? country.iso2 : initialValues.phoneCountryCode;
    const phoneDialCode = country ? country.dialCode : initialValues.phoneDialCode;

    const stateData = country && placeDetails.state
      ? getStateByName(country.id, placeDetails.state)
      : null;
    const stateId = stateData ? String(stateData.id) : '';
    const stateName = stateData ? stateData.name : placeDetails.state || '';

    return {
      ...initialValues,
      businessName: placeDetails.name || '',
      phoneNumber: cleanPhone,
      phoneCountryCode,
      phoneDialCode,
      website: placeDetails.website || '',
      streetAddress: placeDetails.streetAddress || '',
      city: placeDetails.city || '',
      state: stateId,
      stateName,
      postalCode: placeDetails.postcode || '',
      country: countryId,
      countryName,
      latitude: placeDetails.latitude || null,
      longitude: placeDetails.longitude || null,
      billingPhoneCountryCode: phoneCountryCode,
      billingPhoneDialCode: phoneDialCode,
    };
  }, [placeDetails]);

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  // Handle form submission
  const handleSubmit = useCallback(
    async (
      values: AddBusinessFormValues,
      { setFieldValue }: FormikHelpers<AddBusinessFormValues>
    ): Promise<void> => {
      try {
        // Build business address for geocoding
        const businessAddress: AddressComponents = {
          streetAddress: values.streetAddress,
          city: values.city,
          state: values.stateName,
          country: values.countryName,
          postalCode: values.postalCode,
        };

        // Build billing address for geocoding
        const billingAddressComponents: AddressComponents = values.sameAsBusiness
          ? businessAddress
          : {
              streetAddress: values.billingStreetAddress,
              city: values.billingCity,
              state: values.billingStateName,
              country: values.billingCountryName,
              postalCode: values.billingPostalCode,
            };

        let businessLat = values.latitude;
        let businessLng = values.longitude;
        let billingLat = values.billingLatitude;
        let billingLng = values.billingLongitude;

        // Geocode business address if lat/long not available
        if (!businessLat || !businessLng) {
          const fullBusinessAddress = buildFullAddress(businessAddress);
          const businessGeocode = await geocodeAddress(fullBusinessAddress);

          if (!businessGeocode) {
            setSnackbarMessage("We can't find your business location. Please add a valid address.");
            setSnackbarSeverity('error');
            setShowSnackbar(true);
            return;
          }

          businessLat = businessGeocode.latitude;
          businessLng = businessGeocode.longitude;
          setFieldValue('latitude', businessLat);
          setFieldValue('longitude', businessLng);
        }

        // Geocode billing address
        const addressesAreSame = areAddressesSame(businessAddress, billingAddressComponents);
        if (!billingLat || !billingLng) {
          if (addressesAreSame) {
            billingLat = businessLat;
            billingLng = businessLng;
          } else {
            const fullBillingAddress = buildFullAddress(billingAddressComponents);
            const billingGeocode = await geocodeAddress(fullBillingAddress);

            if (!billingGeocode) {
              setSnackbarMessage("We can't find your billing location. Please add a valid address.");
              setSnackbarSeverity('error');
              setShowSnackbar(true);
              return;
            }

            billingLat = billingGeocode.latitude;
            billingLng = billingGeocode.longitude;
          }
          setFieldValue('billingLatitude', billingLat);
          setFieldValue('billingLongitude', billingLng);
        }

        const requestData: CreateBusinessRequest = {
          full_name: values.fullName,
          business_name: values.businessName,
          business_category: values.businessCategory,
          business_email: values.businessEmail,
          phone_number: values.phoneNumber,
          phone_country_code: values.phoneCountryCode,
          phone_dial_code: values.phoneDialCode,
          street_address: values.streetAddress,
          city: values.city,
          state_name: values.stateName,
          postal_code: values.postalCode,
          country_name: values.countryName,
          gst_number: values.gstNumber || '',
          same_as_business: values.sameAsBusiness,
          latitude: businessLat,
          longitude: businessLng,
          ...(isOtherCategoryMode && values.otherCategoryName
            ? { other_category_name: values.otherCategoryName }
            : {}),
        };

        // Add billing details
        requestData.registered_business_name = values.sameAsBusiness
          ? values.businessName
          : values.registeredBusinessName;
        requestData.billing_email = values.sameAsBusiness ? values.businessEmail : values.billingEmail;
        requestData.billing_phone_number = values.sameAsBusiness
          ? values.phoneNumber
          : values.billingPhoneNumber;
        requestData.billing_phone_country_code = values.sameAsBusiness
          ? values.phoneCountryCode
          : values.billingPhoneCountryCode;
        requestData.billing_phone_dial_code = values.sameAsBusiness
          ? values.phoneDialCode
          : values.billingPhoneDialCode;
        requestData.billing_street_address = values.sameAsBusiness
          ? values.streetAddress
          : values.billingStreetAddress;
        requestData.billing_city = values.sameAsBusiness ? values.city : values.billingCity;
        requestData.billing_state_name = values.sameAsBusiness
          ? values.stateName
          : values.billingStateName;
        requestData.billing_postal_code = values.sameAsBusiness
          ? values.postalCode
          : values.billingPostalCode;
        requestData.billing_country_name = values.sameAsBusiness
          ? values.countryName
          : values.billingCountryName;
        requestData.billing_latitude = billingLat;
        requestData.billing_longitude = billingLng;

        await createBusiness(requestData);

        // The creator just became Administrator — refresh & persist the role so the
        // console renders with admin access immediately (otherwise the guards see a
        // stale/empty role and treat them as a provider until the next login).
        await getUserRole().catch(() => null);

        setSnackbarMessage('Business created successfully!');
        setSnackbarSeverity('success');
        setShowSnackbar(true);

        setTimeout(() => {
          navigate(landingPath());
        }, 1500);
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || 'Failed to create business';
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setShowSnackbar(true);
      }
    },
    [createBusiness, getUserRole, navigate, isOtherCategoryMode]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.backgroundDefault,
        pb: 4,
      }}
    >
      {/* Back to Search Link */}
      <Box sx={{ maxWidth: 1024, mx: 'auto', pt: 4, px: 2 }}>
        <Link
          to="/search-business"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: colors.textSecondary,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          Back to Search
        </Link>
      </Box>

      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mt: 2, mb: 4, maxWidth: 1024, mx: 'auto', px: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <BusinessIcon sx={{ color: colors.textWhite, fontSize: 28 }} />
        </Box>

        <Typography
          sx={{
            color: colors.textPrimary,
            fontWeight: 700,
            fontSize: 36,
            lineHeight: '40px',
            mb: 1,
          }}
        >
          Business Details
        </Typography>

        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: 16,
            lineHeight: '24px',
          }}
        >
          Complete your business information
        </Typography>
      </Box>

      {/* Form Card */}
      <Card
        sx={{
          maxWidth: 1024,
          mx: 'auto',
          p: 5,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Formik
          initialValues={formInitialValues}
          validationSchema={addBusinessSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            submitCount,
          }) => {
            // Get available states based on selected country
            const availableStates = values.country
              ? getStatesByCountryId(Number(values.country))
              : [];

            const billingAvailableStates = values.billingCountry
              ? getStatesByCountryId(Number(values.billingCountry))
              : [];

            // Helper to get error message - show errors after submit attempt or if field was touched
            const getError = (field: keyof AddBusinessFormValues): string | undefined => {
              const hasError = errors[field];
              const wasTouched = touched[field];
              const wasSubmitted = submitCount > 0;
              return (wasTouched || wasSubmitted) && hasError ? (hasError as string) : undefined;
            };

            // Handle "Add Other" click
            const handleAddOther = () => {
              if (otherCategory) {
                setFieldValue('businessCategory', otherCategory.id);
                setFieldValue('otherCategoryName', '');
                setIsOtherCategoryMode(true);
              }
            };

            // Handle reset other category
            const handleResetOtherCategory = () => {
              setFieldValue('businessCategory', '');
              setFieldValue('otherCategoryName', '');
              setIsOtherCategoryMode(false);
            };

            // Handle country change
            const handleCountryChange = (countryId: number) => {
              const country = COUNTRIES.find((c) => c.id === countryId);
              if (country) {
                setFieldValue('country', String(country.id));
                setFieldValue('countryName', country.name);
                setFieldValue('state', '');
                setFieldValue('stateName', '');
              }
            };

            // Handle billing country change
            const handleBillingCountryChange = (countryId: number) => {
              const country = COUNTRIES.find((c) => c.id === countryId);
              if (country) {
                setFieldValue('billingCountry', String(country.id));
                setFieldValue('billingCountryName', country.name);
                setFieldValue('billingState', '');
                setFieldValue('billingStateName', '');
              }
            };

            // Handle state change
            const handleStateChange = (stateId: number) => {
              const stateData = availableStates.find((s) => s.id === stateId);
              if (stateData) {
                setFieldValue('state', String(stateData.id));
                setFieldValue('stateName', stateData.name);
              }
            };

            // Handle billing state change
            const handleBillingStateChange = (stateId: number) => {
              const stateData = billingAvailableStates.find((s) => s.id === stateId);
              if (stateData) {
                setFieldValue('billingState', String(stateData.id));
                setFieldValue('billingStateName', stateData.name);
              }
            };

            // Handle phone country change
            const handlePhoneCountryChange = (dialCode: string) => {
              const country = COUNTRIES.find((c) => c.dialCode === dialCode);
              if (country) {
                setFieldValue('phoneCountryCode', country.iso2);
                setFieldValue('phoneDialCode', country.dialCode);
                setFieldValue('phoneLocale', country.locale);
              }
            };

            // Handle billing phone country change
            const handleBillingPhoneCountryChange = (dialCode: string) => {
              const country = COUNTRIES.find((c) => c.dialCode === dialCode);
              if (country) {
                setFieldValue('billingPhoneCountryCode', country.iso2);
                setFieldValue('billingPhoneDialCode', country.dialCode);
                setFieldValue('billingPhoneLocale', country.locale);
              }
            };

            // Get selected country for phone display
            const selectedPhoneCountry =
              COUNTRIES.find((c) => c.iso2 === values.phoneCountryCode) || DEFAULT_COUNTRY;
            const selectedBillingPhoneCountry =
              COUNTRIES.find((c) => c.iso2 === values.billingPhoneCountryCode) || DEFAULT_COUNTRY;

            return (
              <Form>
                {/* Owner Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonOutlineIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Owner Information
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Full Name <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your full name"
                      error={!!getError('fullName')}
                      helperText={getError('fullName')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Business Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Business Information
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Business Name */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Business Name <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="businessName"
                        value={values.businessName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter business name"
                        error={!!getError('businessName')}
                        helperText={getError('businessName')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Business Category */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Business Category <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      {isOtherCategoryMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <TextField
                            fullWidth
                            name="otherCategoryName"
                            value={values.otherCategoryName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter your category name"
                            error={!!getError('otherCategoryName')}
                            helperText={getError('otherCategoryName')}
                            autoFocus
                            sx={inputStyles}
                          />
                          <Button
                            onClick={handleResetOtherCategory}
                            sx={{
                              minWidth: 56,
                              height: 56,
                              borderRadius: '14px',
                              border: `1px solid ${colors.inputBorder}`,
                              color: colors.textSecondary,
                              '&:hover': {
                                borderColor: colors.error,
                                color: colors.error,
                              },
                            }}
                          >
                            <CloseIcon />
                          </Button>
                        </Box>
                      ) : (
                        <FormControl fullWidth error={!!getError('businessCategory')} sx={formControlStyles}>
                          <Select
                            name="businessCategory"
                            value={values.businessCategory}
                            onChange={(e) => {
                              if (e.target.value === '__add_other__') {
                                handleAddOther();
                                return;
                              }
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                            disabled={isCategoriesLoading}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>
                                {isCategoriesLoading ? 'Loading categories...' : 'Select a category'}
                              </span>
                            </MenuItem>
                            {categoryOptions.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                            <MenuItem
                              value="__add_other__"
                              sx={{
                                borderTop: `1px solid ${colors.border}`,
                                mt: 1,
                                pt: 1.5,
                                color: colors.primary,
                                fontWeight: 500,
                              }}
                            >
                              <AddIcon sx={{ fontSize: 18, mr: 1 }} />
                              Add Other
                            </MenuItem>
                          </Select>
                          {getError('businessCategory') && (
                            <FormHelperText>{getError('businessCategory')}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Contact Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhoneOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Contact Information
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Email */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Email Address <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="businessEmail"
                        value={values.businessEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="business@example.com"
                        error={!!getError('businessEmail')}
                        helperText={getError('businessEmail')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Phone Number with Country Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Phone Number <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl sx={{ width: 120, flexShrink: 0 }}>
                          <Select
                            value={selectedPhoneCountry.dialCode}
                            onChange={(e) => handlePhoneCountryChange(e.target.value)}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={countryCodeSelectStyles}
                            renderValue={() => (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontSize: 20 }}>{selectedPhoneCountry.flag}</span>
                                <span style={{ color: colors.gray700, fontSize: 14 }}>
                                  {selectedPhoneCountry.dialCode}
                                </span>
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: { maxHeight: 300 },
                              },
                            }}
                          >
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={country.dialCode}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <span style={{ fontSize: 20 }}>{country.flag}</span>
                                  <span>{country.name}</span>
                                  <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>
                                    {country.dialCode}
                                  </span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          name="phoneNumber"
                          value={values.phoneNumber}
                          onChange={(e) =>
                            setFieldValue('phoneNumber', e.target.value.replace(/\D/g, ''))
                          }
                          onBlur={handleBlur}
                          placeholder="Enter mobile number"
                          error={!!getError('phoneNumber')}
                          helperText={getError('phoneNumber')}
                          inputProps={{ maxLength: 12 }}
                          sx={inputStyles}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Website */}
                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Website (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      name="website"
                      value={values.website}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="https://www.example.com"
                      error={!!getError('website')}
                      helperText={getError('website')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Business Address Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                    <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                      Business Address
                    </Typography>
                  </Box>

                  {/* Street Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Street Address <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="streetAddress"
                      value={values.streetAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter complete street address"
                      error={!!getError('streetAddress')}
                      helperText={getError('streetAddress')}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Country and State Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Country */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Country <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!getError('country')} sx={formControlStyles}>
                        <Select
                          value={values.country}
                          onChange={(e) => handleCountryChange(Number(e.target.value))}
                          onBlur={handleBlur}
                          name="country"
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: colors.textTertiary }}>Select Country</span>
                          </MenuItem>
                          {COUNTRIES.map((country) => (
                            <MenuItem key={country.id} value={String(country.id)}>
                              {country.flag} {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {getError('country') && (
                          <FormHelperText>{getError('country')}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* State */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        State <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!getError('state')} sx={formControlStyles}>
                        <Select
                          value={values.state}
                          onChange={(e) => handleStateChange(Number(e.target.value))}
                          onBlur={handleBlur}
                          name="state"
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                          disabled={!values.country || availableStates.length === 0}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: colors.textTertiary }}>
                              {!values.country ? 'Select country first' : 'Select State'}
                            </span>
                          </MenuItem>
                          {availableStates.map((state) => (
                            <MenuItem key={state.id} value={String(state.id)}>
                              {state.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {getError('state') && <FormHelperText>{getError('state')}</FormHelperText>}
                      </FormControl>
                    </Box>
                  </Box>

                  {/* City and PIN Code Row */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* City */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        City <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter city"
                        error={!!getError('city')}
                        helperText={getError('city')}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* PIN Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        PIN Code <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="postalCode"
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="400001"
                        error={!!getError('postalCode')}
                        helperText={getError('postalCode')}
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Billing Details Section */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptOutlinedIcon sx={{ color: colors.textPrimary, fontSize: 20 }} />
                      <Typography sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: 18 }}>
                        Billing Details
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="sameAsBusiness"
                          checked={values.sameAsBusiness}
                          onChange={handleChange}
                          sx={{
                            color: colors.textSecondary,
                            '&.Mui-checked': { color: colors.primary },
                          }}
                        />
                      }
                      label="Same as business details"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: 14, color: colors.gray700 } }}
                    />
                  </Box>

                  {/* Registered Business Name */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Registered Business Name
                    </Typography>
                    <TextField
                      fullWidth
                      name="registeredBusinessName"
                      value={values.sameAsBusiness ? values.businessName : values.registeredBusinessName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter registered business name"
                      disabled={values.sameAsBusiness}
                      error={!values.sameAsBusiness && !!getError('registeredBusinessName')}
                      helperText={!values.sameAsBusiness ? getError('registeredBusinessName') : ''}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Billing Email and Phone Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Billing Email */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Billing Email <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingEmail"
                        value={values.sameAsBusiness ? values.businessEmail : values.billingEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="billing@example.com"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingEmail')}
                        helperText={!values.sameAsBusiness ? getError('billingEmail') : ''}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Billing Phone Number with Country Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        Billing Phone <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl sx={{ width: 120, flexShrink: 0 }}>
                          <Select
                            value={
                              values.sameAsBusiness
                                ? selectedPhoneCountry.dialCode
                                : selectedBillingPhoneCountry.dialCode
                            }
                            onChange={(e) => handleBillingPhoneCountryChange(e.target.value)}
                            disabled={values.sameAsBusiness}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={countryCodeSelectStyles}
                            renderValue={() => (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontSize: 20 }}>
                                  {values.sameAsBusiness
                                    ? selectedPhoneCountry.flag
                                    : selectedBillingPhoneCountry.flag}
                                </span>
                                <span style={{ color: colors.gray700, fontSize: 14 }}>
                                  {values.sameAsBusiness
                                    ? selectedPhoneCountry.dialCode
                                    : selectedBillingPhoneCountry.dialCode}
                                </span>
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                sx: { maxHeight: 300 },
                              },
                            }}
                          >
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={country.dialCode}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <span style={{ fontSize: 20 }}>{country.flag}</span>
                                  <span>{country.name}</span>
                                  <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>
                                    {country.dialCode}
                                  </span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          name="billingPhoneNumber"
                          value={
                            values.sameAsBusiness ? values.phoneNumber : values.billingPhoneNumber
                          }
                          onChange={(e) =>
                            setFieldValue('billingPhoneNumber', e.target.value.replace(/\D/g, ''))
                          }
                          onBlur={handleBlur}
                          placeholder="Enter mobile number"
                          disabled={values.sameAsBusiness}
                          error={!values.sameAsBusiness && !!getError('billingPhoneNumber')}
                          helperText={!values.sameAsBusiness ? getError('billingPhoneNumber') : ''}
                          inputProps={{ maxLength: 12 }}
                          sx={inputStyles}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Billing Street Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      Street Address <span style={{ color: colors.error }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="billingStreetAddress"
                      value={
                        values.sameAsBusiness ? values.streetAddress : values.billingStreetAddress
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter complete street address"
                      disabled={values.sameAsBusiness}
                      error={!values.sameAsBusiness && !!getError('billingStreetAddress')}
                      helperText={!values.sameAsBusiness ? getError('billingStreetAddress') : ''}
                      sx={inputStyles}
                    />
                  </Box>

                  {/* Billing Country and State Row */}
                  {!values.sameAsBusiness && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {/* Billing Country */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}
                        >
                          Country <span style={{ color: colors.error }}>*</span>
                        </Typography>
                        <FormControl fullWidth error={!!getError('billingCountry')} sx={formControlStyles}>
                          <Select
                            value={values.billingCountry}
                            onChange={(e) => handleBillingCountryChange(Number(e.target.value))}
                            onBlur={handleBlur}
                            name="billingCountry"
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>Select Country</span>
                            </MenuItem>
                            {COUNTRIES.map((country) => (
                              <MenuItem key={country.id} value={String(country.id)}>
                                {country.flag} {country.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {getError('billingCountry') && (
                            <FormHelperText>{getError('billingCountry')}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      {/* Billing State */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}
                        >
                          State <span style={{ color: colors.error }}>*</span>
                        </Typography>
                        <FormControl fullWidth error={!!getError('billingState')} sx={formControlStyles}>
                          <Select
                            value={values.billingState}
                            onChange={(e) => handleBillingStateChange(Number(e.target.value))}
                            onBlur={handleBlur}
                            name="billingState"
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={selectStyles}
                            disabled={!values.billingCountry || billingAvailableStates.length === 0}
                          >
                            <MenuItem value="" disabled>
                              <span style={{ color: colors.textTertiary }}>
                                {!values.billingCountry ? 'Select country first' : 'Select State'}
                              </span>
                            </MenuItem>
                            {billingAvailableStates.map((state) => (
                              <MenuItem key={state.id} value={String(state.id)}>
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {getError('billingState') && (
                            <FormHelperText>{getError('billingState')}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Box>
                  )}

                  {/* Billing City and PIN Code Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Billing City */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        City <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingCity"
                        value={values.sameAsBusiness ? values.city : values.billingCity}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter city"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingCity')}
                        helperText={!values.sameAsBusiness ? getError('billingCity') : ''}
                        sx={inputStyles}
                      />
                    </Box>

                    {/* Billing PIN Code */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                        PIN Code <span style={{ color: colors.error }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        name="billingPostalCode"
                        value={values.sameAsBusiness ? values.postalCode : values.billingPostalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="400001"
                        disabled={values.sameAsBusiness}
                        error={!values.sameAsBusiness && !!getError('billingPostalCode')}
                        helperText={!values.sameAsBusiness ? getError('billingPostalCode') : ''}
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>

                  {/* GST Number */}
                  <Box>
                    <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
                      GST Number <span style={{ color: colors.textSecondary, fontSize: '0.85em' }}>(Optional)</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="gstNumber"
                      value={values.gstNumber}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setFieldValue('gstNumber', value);
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setFieldTouched('gstNumber', true, true);
                      }}
                      placeholder="Enter GST Number"
                      error={!!getError('gstNumber')}
                      helperText={getError('gstNumber')}
                      sx={inputStyles}
                    />
                  </Box>
                </Box>

                {/* Create Account Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isSubmitting || isCreating}
                  sx={{
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: colors.primary,
                    color: colors.textWhite,
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
                    fontWeight: 600,
                    fontSize: 16,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: colors.primaryDark,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: colors.gray200,
                      color: colors.textTertiary,
                    },
                  }}
                >
                  {isSubmitting || isCreating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Shared input styles
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.inputBorder,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px',
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 'normal',
    height: 24,
    boxSizing: 'content-box',
    '&::placeholder': {
      color: colors.textTertiary,
      opacity: 1,
    },
  },
  // For multiline textareas
  '& .MuiOutlinedInput-multiline': {
    padding: 0,
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
      height: 'auto',
    },
  },
  // Error helper text styling
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: '4px',
    marginBottom: 0,
    fontSize: 12,
  },
};

const selectStyles = {
  height: 56,
  borderRadius: '10px',
  backgroundColor: colors.backgroundPaper,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E8F0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '& .MuiSelect-select': {
    fontSize: 14,
  },
};

// FormControl styles for Select with error helper text
const formControlStyles = {
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: '4px',
    fontSize: 12,
  },
};

// Country code selector styles
const countryCodeSelectStyles = {
  height: 56,
  width: '100%',
  backgroundColor: colors.backgroundPaper,
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E2E8F0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '32px !important',
  },
  '& .MuiSelect-icon': {
    right: 8,
  },
};

export default AddBusinessScreen;
